import React, { useEffect, useRef, useState } from "react";
import {
  FiBell,
  FiChevronDown,
  FiLogOut,
  FiPlus,
  FiRefreshCw,
  FiUser,
} from "react-icons/fi";
import AdminUserSwitcher from "./AdminUserSwitcher";
import Avatar from "./Avatar";
import TimesheetApprovalProgress from "./TimesheetApprovalProgress";
import { getProfileImageCandidates } from "../utils/profileImages";

const HrmHeaderRevamped = ({
  adminUsersLoading,
  isAdmin,
  managedUsers,
  holidays,
  monthTimesheet,
  notifications = [],
  onAction,
  onLogout,
  onManagedUserChange,
  onNavigate = () => {},
  onReloadData,
  reloadingData,
  selectedManagedUserId,
  selectedTimesheetMonth,
  selectedTimesheetMonthId,
  user,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const profileButtonRef = useRef(null);
  const profileRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationsRef = useRef(null);
  const userName = String(user?.name || user?.email || "Employee").trim();
  const firstName = userName.split(/\s+/)[0] || "Employee";
  const initials = userName
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const profileImageUrls = getProfileImageCandidates(user || {});

  useEffect(() => {
    if (!menuOpen && !notificationsOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (menuOpen && !profileRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
      if (notificationsOpen && !notificationsRef.current?.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      if (notificationsOpen) {
        setNotificationsOpen(false);
        notificationButtonRef.current?.focus();
      }
      if (menuOpen) {
        setMenuOpen(false);
        profileButtonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, notificationsOpen]);

  const runNotification = (notification) => {
    setNotificationsOpen(false);
    notification.onSelect?.();
  };

  return (
    <header className="new-hrm-header">
      <div className={`new-hrm-header__actions new-hrm-header__actions--${isAdmin ? "admin" : "employee"}`}>
        <div className="new-hrm-header__brand" aria-label="Antrazal People">
          <img src="/assets/antrazal-brand.svg" alt="Antrazal" />
          <span><strong>People</strong></span>
        </div>

        <TimesheetApprovalProgress
          holidays={holidays}
          monthTimesheet={monthTimesheet}
          selectedTimesheetMonth={selectedTimesheetMonth}
          selectedTimesheetMonthId={selectedTimesheetMonthId}
        />

        <div className="new-hrm-notifications" ref={notificationsRef}>
          <button
            ref={notificationButtonRef}
            aria-label={`Notifications${notifications.length ? `, ${notifications.length} requiring attention` : ""}`}
            aria-expanded={notificationsOpen}
            aria-controls="new-hrm-notifications-menu"
            className="new-hrm-icon-button new-hrm-notifications__button"
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setNotificationsOpen((open) => !open);
            }}
          >
            <FiBell aria-hidden="true" />
            {notifications.length > 0 && <span aria-hidden="true" />}
          </button>
          {notificationsOpen && (
            <div className="new-hrm-notifications__menu" id="new-hrm-notifications-menu" role="menu">
              <header>
                <strong>Notifications</strong>
                <small>{notifications.length ? `${notifications.length} need attention` : "You are up to date"}</small>
              </header>
              {notifications.length ? notifications.map((notification) => (
                <button key={notification.id} type="button" role="menuitem" onClick={() => runNotification(notification)}>
                  <span className={`new-hrm-notifications__marker new-hrm-notifications__marker--${notification.tone || "brand"}`} aria-hidden="true" />
                  <span><strong>{notification.title}</strong><small>{notification.detail}</small></span>
                </button>
              )) : (
                <p>No outstanding HRM actions.</p>
              )}
            </div>
          )}
        </div>

        <button
          aria-label="Reload HRM data"
          className="new-hrm-icon-button new-hrm-refresh-button"
          disabled={reloadingData}
          title="Reload HRM data"
          type="button"
          onClick={onReloadData}
        >
          <FiRefreshCw aria-hidden="true" />
        </button>

        <button
          aria-label="Open quick actions"
          className="new-hrm-quick-action"
          title="Create"
          type="button"
          onClick={() => onAction("quick")}
        >
          <FiPlus aria-hidden="true" />
        </button>

        {isAdmin && (
          <AdminUserSwitcher
            managedUsers={managedUsers}
            loading={adminUsersLoading}
            onManagedUserChange={onManagedUserChange}
            selectedManagedUserId={selectedManagedUserId}
            user={user}
          />
        )}

        <div className="new-hrm-profile" ref={profileRef}>
          <button
            ref={profileButtonRef}
            type="button"
            aria-label={`${userName} profile menu`}
            aria-controls="new-hrm-profile-menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setNotificationsOpen(false);
              setMenuOpen((open) => !open);
            }}
          >
            <Avatar imageUrls={profileImageUrls} initials={initials} name={userName} />
            <span className="new-hrm-profile__identity">
              <strong>{firstName}</strong>
              <small>{user?.role || "Employee"}</small>
            </span>
            <FiChevronDown aria-hidden="true" />
          </button>
          {menuOpen && (
            <div className="new-hrm-profile__menu" id="new-hrm-profile-menu" role="menu">
              <header>
                <strong>{userName}</strong>
                <small>{user?.email || ""}</small>
              </header>
              <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onNavigate("profile"); }}>
                <FiUser aria-hidden="true" />
                <span>My profile</span>
              </button>
              <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onReloadData(); }} disabled={reloadingData}>
                <FiRefreshCw aria-hidden="true" />
                <span>Refresh data</span>
              </button>
              <button className="new-hrm-profile__logout" type="button" role="menuitem" onClick={onLogout}>
                <FiLogOut aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default HrmHeaderRevamped;
